import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { ClipboardList, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Everyone parked with "skip for now", so no credit decision gets forgotten. */
export const CreditsToDecide = ({ onOpenDate, refreshKey }) => {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/academy/admin/credits/to-decide`, { withCredentials: true });
      setData(r.data);
    } catch (_) { /* worklist is a convenience, never block the page */ }
  };

  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line

  if (!data || (!data.dates?.length && !data.refused?.length && !data.needs_linking?.length)) return null;

  return (
    <Card className="bg-sky-500/5 border-sky-700/40 p-4 sm:p-6 mb-6" data-testid="credits-to-decide">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-sky-400" />
          {data.total_players > 0
            ? `Still to decide — ${data.total_players} player(s)`
            : "Credit decisions"}
          {data.total_players > 0 && (
            <span className="font-mono text-sky-300">${data.total_amount.toFixed(2)}</span>
          )}
        </h3>
        <Button variant="ghost" size="sm" className="h-8 text-slate-400" onClick={load}
          data-testid="credits-to-decide-refresh">
          <RefreshCw className="w-3 h-3" />
        </Button>
      </div>
      {data.total_players > 0 && (
        <p className="text-xs text-slate-400 mb-3">
          You skipped these while you checked their records. Nothing is lost — open the date and
          finish the call.
        </p>
      )}
      <div className="space-y-2">
        {data.dates.map((row) => (
          <div key={`${row.term_id}-${row.date}`}
            className="bg-slate-900/60 border border-slate-700 rounded px-3 py-2 flex flex-wrap items-center gap-2"
            data-testid={`to-decide-${row.date}`}>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white truncate">
                {row.date} · {row.term_name} <span className="text-slate-500">({row.reason || "cancelled"})</span>
              </p>
              <p className="text-[11px] text-slate-400 truncate">
                {row.players.map((p) => p.player_name).join(", ")}
              </p>
            </div>
            <span className="text-[11px] font-mono text-sky-300 shrink-0">
              ${row.amount_outstanding.toFixed(2)}
            </span>
            <Button size="sm" variant="outline" className="h-8 text-[11px] border-sky-600/50 text-sky-200"
              onClick={() => onOpenDate(row.date)} data-testid={`to-decide-open-${row.date}`}>
              Open this date
            </Button>
          </div>
        ))}
      </div>

      {(data.refused || []).length > 0 && (
        <div className="mt-4 border-t border-sky-700/30 pt-3" data-testid="credits-refused">
          <p className="mb-2 text-xs font-semibold text-slate-300">
            Refused — {data.refused_players} player(s){" "}
            <span className="font-mono text-slate-400">
              ${data.refused_amount.toFixed(2)}
            </span>
          </p>
          <p className="mb-2 text-[11px] text-slate-400">
            You decided against these. Open the date and set them to &ldquo;credit&rdquo; if you
            have changed your mind — it will register this time.
          </p>
          <div className="space-y-2">
            {data.refused.map((row) => (
              <div
                key={`refused-${row.term_id}-${row.date}`}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-700
                           bg-slate-900/40 px-3 py-2"
                data-testid={`refused-${row.date}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-slate-200">
                    {row.date} · {row.term_name}
                  </p>
                  <p className="truncate text-[11px] text-slate-500">
                    {row.players.map((p) => (
                      p.reason ? `${p.player_name} (${p.reason})` : p.player_name
                    )).join(", ")}
                  </p>
                </div>
                <span className="shrink-0 font-mono text-[11px] text-slate-400">
                  ${row.amount_not_credited.toFixed(2)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-slate-600 text-[11px] text-slate-200"
                  onClick={() => onOpenDate(row.date)}
                  data-testid={`refused-open-${row.date}`}
                >
                  Open this date
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {(data.needs_linking || []).length > 0 && (
        <div className="mt-4 border-t border-rose-700/30 pt-3" data-testid="credits-needs-linking">
          <p className="mb-2 text-xs font-semibold text-rose-200">
            Needs linking — {data.needs_linking_players} registration(s){" "}
            <span className="font-mono text-rose-300/80">
              ${data.needs_linking_amount.toFixed(2)}
            </span>
          </p>
          <p className="mb-2 text-[11px] text-slate-400">
            These families paid and are owed a credit, but the registration has no child record
            attached, so there is nobody to credit it to. Link the child on the registration, then
            open the date and issue it.
          </p>
          <div className="space-y-2">
            {data.needs_linking.map((row) => (
              <div
                key={`linking-${row.term_id}-${row.date}`}
                className="flex flex-wrap items-center gap-2 rounded border border-rose-800/50
                           bg-slate-900/40 px-3 py-2"
                data-testid={`needs-linking-${row.date}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-slate-200">
                    {row.date} · {row.term_name}
                  </p>
                  <p className="truncate text-[11px] text-slate-500">
                    {row.players.map((p) => (
                      p.parent_name ? `${p.parent_name} (${p.registration_id})` : p.registration_id
                    )).join(", ")}
                  </p>
                </div>
                <span className="shrink-0 font-mono text-[11px] text-slate-400">
                  ${row.amount_not_credited.toFixed(2)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-rose-700/60 text-[11px] text-rose-200"
                  onClick={() => onOpenDate(row.date)}
                  data-testid={`needs-linking-open-${row.date}`}
                >
                  Open this date
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
